import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { CtrlBaseComponent } from '../ctrl-base.component';
import { breakpoints } from '../../environments/environment.breakpoints';
import { ModalIconType, ModalButtonColor, ModalType } from '../../enums/modal';
import { PfComponents } from '../../enums/pfComponents';
import { Action, ActionType, ElementViewportPosition } from '../../models';
import { Router } from '@angular/router';
import { CtButtonComponent } from '../ct-button/ct-button.component';
import { Subscription } from 'rxjs';
import { MenuService } from '../../services';

@Component({
  selector: 'ct-dialog',
  templateUrl: './ct-dialog.component.html',
  styleUrls: ['./ct-dialog.component.css']
})
export class CtDialogComponent extends CtrlBaseComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('target', { read: ViewContainerRef }) target: ViewContainerRef;
  @ViewChild('actionButtonContainer', { read: ViewContainerRef }) actionButtonContainer: ViewContainerRef;
  @ViewChild('defaultButtonContainer', { read: ViewContainerRef }) defaultButtonContainer: ViewContainerRef;


  //constants
  public iconWidth : number = 70;
  public iconHeight : number = 68;
  public closeIconPath : string = 'assets/icons/close.svg';

    // util
    private isOpen : boolean = false;
    public isMedium: boolean = false;
    public isSmall: boolean = false;
    public isExtraSmall: boolean = false;
    public subscriptions : Subscription[] = [];
    
    // properties
    private autoOpen: boolean = false;
    private title: string;
    private modalType : ModalType;
    private modalIconType: ModalIconType;
    private modalButtonColor: ModalButtonColor = ModalButtonColor.primary;
    private description : string;
    public canBeClosed : boolean = true;
    private actionButtonText : string = "Confirmar";
    private cancelButtonText : string = "Regresar";
    private width : number = 500;
    private tagText : string = '';
    private tagColor : ModalButtonColor;
    private guidedTourTargetComponentId : string;
    private guidedTourTargetMenuOption : string;
    private hasFooterSpacing : boolean = true;
    // guided tour
    public xAxis : number = 0;
    public yAxis : number = 0;
    public isGuidedArrowLeft : boolean = false;
    private readonly GUIDED_ARROW_POSITION_THRESHOLD = 0.60;

  public titleIcons : Map<string,string> = new Map<string,string>([
    ['success', 'assets/icons/check.svg'],
    ['caution', 'assets/icons/feedback-caution.svg'],
    ['delete', 'assets/icons/feedback-delete.svg'],
    ['inspect', 'assets/icons/feedback-inspect.svg'],
    ['leave', 'assets/icons/feedback-leave.svg'],
    ['question', 'assets/icons/feedback-question.svg'],
    ['reverse', 'assets/icons/feedback-reverse.svg'],
    ['validate', 'assets/icons/feedback-validate.svg'],
    ['send', 'assets/icons/email-send.svg'],
    ['downloadError', 'assets/icons/feedback-caution.svg'],
    ['statistics', 'assets/icons/feedback-statistics.svg'],
    ['bell', 'assets/icons/feedback-bell.svg'],
    ['folder', 'assets/icons/feedback-folder.svg'],
    ['lightning', 'assets/icons/feedback-lightning.svg'],
    ['question2', 'assets/icons/feedback-question2.svg'],
    ['user', 'assets/icons/feedback-user.svg'],
    ['clock', 'assets/icons/feedback-clock.svg']
  ]);

  public excludedRoutesForActions : Set<string> = new Set<string>(['public']);

  //functions
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  constructor(private renderer : Renderer2,
    private router : Router,
    private changeDetectorRef : ChangeDetectorRef) {
    super();
  }

  ngAfterViewInit(): void {
    this.paintContent();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * The ngOnInit function checks the screen size, sets an icon to a modal, and opens a dialog if
   * autoOpen is true.
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.checkScreenSize();
    this.setIconToModal();
    if(this.autoOpen) this.openDialog();
  }

  /**
   * The function overrides the type set by the dev and sets the icon type to match the modal type.
   */
  private setIconToModal(): void {
    if(this.modalType === ModalType.success) this.modalIconType = ModalIconType.success;
    if(this.modalType === ModalType.error) this.modalIconType = ModalIconType.caution;
    if(this.modalType === ModalType.send) this.modalIconType = ModalIconType.send;
    if(this.modalType === ModalType.downloadError) this.modalIconType = ModalIconType.downloadError;


  }

  /**
   * The function paints the content of the dialog after the view has been initialized and the dialog has been opened.
   */
    private paintContent(): void {
    if(this.getMetaData().models.length > 0){
      this.paintChildren(this.getMetaData(),this.target);
    }
    this.getActionButtons();
  }

  /**
   * This function generates action buttons for a modal based on the modal type.
   */
  private getActionButtons(): void {

    const metadata = {
      ctType: PfComponents.CtButton,
      controlProperties: {  
        hidden : false,
        listenedComponentId: "",
        listenedProperty: "",
        disabled: false,
        modelId: "",
        btnBlock: true,
        buttonText: this.getActionButtonText(),
        buttonColor: this.getModalButtonColor(),
        icon: "",
        id: `${this.getViewId()}_${PfComponents.CtButton}_default_${0}`,
      },
      controlActions: [this.getButtonAction()],
      models: []
    };

    if(!this.actionButtonContainer) return;
    if(this.modalType === ModalType.confirmation ||
      this.modalType === ModalType.guidedTour) {
      this.paintChildren({models : [metadata]},this.actionButtonContainer);
      this.paintChildren({models : [
        this.getCloseButtonAction() == null ? this.getDefaultButton() : this.getCloseButtonAction()
      ]},this.defaultButtonContainer);
    }
    this.paintChildren({models : [metadata]},this.actionButtonContainer);
    this.manageButtonActions();
  }

  /**
   * When the modal is confirmation you can set a close button action to execute when you close the dialog.
   */
  private getCloseButtonAction() {
    const action : Action = this.getEvent('onCloseClick');
    if(!action) return null;
    action.eventName = 'onClick';
    return {
      ctType: PfComponents.CtButton,
      controlProperties: {  
        hidden : false,
        disabled: false,
        listenedComponentId: "",
        listenedProperty: "",
        modelId : '',
        width: this.isMedium ? 250 : 150,
        buttonText: this.getCancelButtonText(),
        buttonColor: 'default',
        icon: "",
        id: `${this.getViewId()}_${PfComponents.CtButton}_default_${0}`,
      },
      controlActions: [action],
      models: []
    };
  }

  /**
   * If the URL is part of the excluded ones, this means that the CtDialog cannot get the component from the utilService,
   * because the screen is not a JSON view. this condition applies for public views and auth module views, thus it cannot perform actions from the
   * utilService. This function simplifies the closeDialog function by checking if the URL is excluded and closing the dialog if it is.
   */
  private manageButtonActions() {
    if(!this.urlIsExcluded()) return;
    for(const child of this.childElements) {
      if(child.getCtType() !== PfComponents.CtButton) continue;
      const ctButtonChild = child as CtButtonComponent;
      if(!this.hasCloseAction(ctButtonChild)) continue;
      const subscription = ctButtonChild.clickTimer.subscribe(() => {
        this.closeDialog();
      });
      this.subscriptions.push(subscription);
    }
  }

  /**
   * As part of the excluded URL flow this function checks if the button has a close action.
   */
  private hasCloseAction(ctButtonChild: CtButtonComponent): boolean {
    for(const action of ctButtonChild.getControlActions()) {
      if(action == null) return false;
      if(action.action === 'utl_closeDialog') return true;
    }
    return false;
  }

  /**
   * All excluded URLs are in the excludedRoutesForActions Set.
   */
  private urlIsExcluded(): boolean {
    const currentUrl = this.router.url;
    for (const excludedRoute of this.excludedRoutesForActions) {
      if (currentUrl.includes(excludedRoute)) {
        return true;
      }
    }
    return false;
  }

  /**
   * The function returns an action based on whether modal actions should be performed or not.
   * @returns The function `getButtonAction()` returns an `Action` object. The specific `Action` object
   * that is returned depends on the result of the `performModalActions()` function.
   */
  private getButtonAction(): Action {
    if(this.performModalActions()){
      return this.getEvent('onClick');
    }
    return this.getCloseDialogAction();
  }

  /**
   * The function checks if the modal type is either "action" or "confirmation". that means that the modal
   * should perform an action when the button is clicked. and not close the dialog.
   */
  private performModalActions(): boolean {
    return this.modalType === ModalType.action 
    || this.modalType === ModalType.confirmation
    || this.modalType === ModalType.guidedTour;
  }

  /**
   * This function checks the screen size and sets boolean variables based on the breakpoints defined.
  */
  private checkScreenSize() : void {
    const screenWidth = window.innerWidth;
    this.isMedium = screenWidth >= breakpoints.L;
    this.isSmall = screenWidth < breakpoints.L && screenWidth > breakpoints.M;
    this.isExtraSmall = screenWidth <= breakpoints.M && screenWidth > breakpoints.S;
  }

  /**
   * Opens the dialog. This function should be called when the dialog needs to be shown to the user.
   * Schedules the execution of ngAfterViewInit to ensure the logic inside it can run when the ng-container is available.
   * This is needed because the ng-container is not available when the dialog is not open.
   * This function also sets the overflow to the body to hidden so the scroll property is not accessible when the dialog is open.
   */
  public openDialog(): void {
    this.isOpen = true;
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
    this.checkGuidedArrowPosition();
    //executes onClose action if it exists
    let event = this.getEvent('onOpen');
    if(event) this.executeAction2(event);
  }

  /**
   * After closing the dialog it sets the overflow to the body back to its default value.
   * So the scroll property is not affected by the dialog.
   */
  public closeDialog(): void {
    this.isOpen = false;
    this.renderer.removeStyle(document.body, 'overflow');
    
    //executes onClose action if it exists
    let event = this.getEvent('onClose');
    if(event) this.executeAction2(event);
  }

  /**
   * This function returns the icon associated with the modal icon type.
   * @returns A string value is being returned, which is the value associated with the key
   * `this.modalIconType` in the `titleIcons` map.
   */
  public getMessageIcon() : string {
    return this.titleIcons.get(this.modalIconType);
  }

  public showDialog(): void {
    this.setHidden(false);
  }

  /**
   * This function returns the default button for the modal when the modal type is not confirmation and error.
   * so it just closes the dialog
   */
  private getDefaultButton(): any {
    return {
      ctType: PfComponents.CtButton,
      controlProperties: {  
        hidden : false,
        disabled: false,
        listenedComponentId: "",
        listenedProperty: "",
        modelId : '',
        width: this.isMedium ? 250 : 150,
        buttonText: this.getCancelButtonText(),
        buttonColor: 'default',
        icon: "",
        id: `${this.getViewId()}_${PfComponents.CtButton}_default_${0}`,
      },
      controlActions: [this.getCloseDialogAction()],
      models: []
    };
  }

  /**
   * The function returns an Action object that triggers the "utl_closeDialog"
   * It is used to close the dialog if the modal type is not action or confirmation.
   * @returns action to close the dialog
   */
  getCloseDialogAction(): Action {
    let action = new Action('onClick','utl_closeDialog',ActionType.VIEW);
    action.actionParams = [this.getId()];
    return action;
  }

  getTagColorClass(): string {
    switch(this.tagColor){
      case ModalButtonColor.primary:
        return 'th-bg-main';
      case ModalButtonColor.secondary:
        return 'th-bg-secondary';
      case ModalButtonColor.info:
        return 'th-bg-info';
      case ModalButtonColor.danger:
        return 'th-bg-alert';
      default:
        return 'th-bg-info';
    }
  }

  executeCancelAction(): void {
    const action : Action = this.getEvent('onCloseClick');
    if(action) this.executeAction2(action);
  }

  public setStringIconType(modalIconType: string): void {
    this.modalIconType = ModalIconType[modalIconType];
  }

  public get modalTypeE(): typeof ModalType {
    return ModalType; 
  }

  /**
   * Checks if the pointer of the triangle should be on the left or right side of the modal.
   * based on the size of the screen if the dialog is placed on the right or left side of the screen.
   */
  checkGuidedArrowPosition(): void {
    if(this.modalType !== ModalType.guidedTour) return;
    this.changeDetectorRef.detectChanges();
    const element = document.getElementById(this.getId());
    if(!element) return;
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const elementLeftPosition = rect.left;

    if (elementLeftPosition < (viewportWidth / 2)*this.GUIDED_ARROW_POSITION_THRESHOLD) {
      this.isGuidedArrowLeft = true;
      return;
    }
    this.isGuidedArrowLeft = false;
  }

  setPosition(position : ElementViewportPosition): void {
    this.xAxis = position.xAxis;
    this.yAxis = position.yAxis;
  }

  getAutoOpen(): boolean { return this.autoOpen }
  getTitle(): string { return this.title }
  getIsOpen(): boolean { return this.isOpen }
  getModalIconType(): ModalIconType | string{ 
    if(this.modalType == null) return '';
    return this.modalIconType
   }
  getCanBeClosed(): boolean { return this.canBeClosed }
  getDescription(): string { return this.description }
  getModalButtonColor(): ModalButtonColor { 
    if(this.modalType === ModalType.error) return ModalButtonColor.danger;
    return this.modalButtonColor;
  }
  getModalType(): ModalType | string { 
    if(this.modalType == null) return '';
    return this.modalType
   }
  getCancelButtonText(): string { return this.cancelButtonText.toUpperCase() }
  getActionButtonText(): string { return this.actionButtonText.toUpperCase() }
  getWidth(): number { return this.width }
  getTagText(): string { return this.tagText }
  getTagColor(): ModalButtonColor { return this.tagColor }
  getGuidedTourTargetComponentId(): string { return this.guidedTourTargetComponentId }
  getGuidedTourTargetMenuOption(): string { return this.guidedTourTargetMenuOption }
  getHasFooterSpacing(): boolean { return this.hasFooterSpacing }

  setAutoOpen(autoOpen: boolean): void { this.autoOpen = autoOpen }
  setTitle(title: string): void { this.title = title }
  setIsOpen(isOpen: boolean): void { this.isOpen = isOpen }
  setModalIconType(modalType: ModalIconType): void { this.modalIconType = modalType }
  setCanBeClosed(canBeClosed: boolean): void { this.canBeClosed = canBeClosed }
  setDescription(description: string): void { this.description = description }
  setModalButtonColor(modalButtonColor: ModalButtonColor): void { this.modalButtonColor = modalButtonColor }
  setModalType(modalType: ModalType): void { this.modalType = modalType }
  setCancelButtonText(cancelButtonText: string): void { this.cancelButtonText = cancelButtonText }
  setActionButtonText(actionButtonText: string): void { this.actionButtonText = actionButtonText }
  setWidth(width: number): void { this.width = width }
  setTagText(tagText: string): void { this.tagText = tagText }
  setTagColor(tagColor: ModalButtonColor): void { this.tagColor = tagColor }
  setGuidedTourTargetComponentId(guidedTourTargetComponentId: string): void { this.guidedTourTargetComponentId = guidedTourTargetComponentId }
  setGuidedTourTargetMenuOption(guidedTourTargetMenuOption: string): void { this.guidedTourTargetMenuOption = guidedTourTargetMenuOption }
  setHasFooterSpacing(hasFooterSpacing: boolean): void { this.hasFooterSpacing = hasFooterSpacing }
}
